import { createTheme, responsiveFontSizes, alpha } from '@mui/material';

const FONT_FAMILY = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Colours used by every chart so pie/bar segments look consistent across pages
export const chartPalette = [
  '#2563eb', '#14b8a6', '#f59e0b', '#8b5cf6', '#ef4444',
  '#06b6d4', '#84cc16', '#ec4899', '#64748b', '#f97316',
];

// Soft, modern elevation scale (replaces Material's heavy default shadows)
const buildShadows = (color) => {
  const s = (y, blur, a) => `0 ${y}px ${blur}px ${alpha(color, a)}`;
  const levels = [
    'none',
    `${s(1, 2, 0.05)}`,
    `${s(1, 3, 0.08)}, ${s(1, 2, 0.04)}`,
    `${s(4, 8, 0.06)}, ${s(2, 4, 0.04)}`,
    `${s(8, 16, 0.08)}, ${s(2, 6, 0.04)}`,
    `${s(12, 24, 0.1)}, ${s(4, 8, 0.04)}`,
    `${s(16, 32, 0.12)}, ${s(4, 10, 0.05)}`,
    `${s(20, 40, 0.14)}, ${s(6, 12, 0.05)}`,
    `${s(24, 48, 0.16)}, ${s(8, 16, 0.06)}`,
  ];
  return Array.from({ length: 25 }, (_, i) => levels[Math.min(i, levels.length - 1)]);
};

// Mobile rules shared by every page, so individual pages don't each need them
const mobileComponentOverrides = (theme) => {
  const phone = theme.breakpoints.down('sm');
  return {
    MuiTableCell: { [phone]: { padding: '8px 10px', fontSize: '0.8rem', whiteSpace: 'nowrap' } },
    MuiDialogPaper: { [phone]: { margin: 12, width: 'calc(100% - 24px)', maxHeight: 'calc(100% - 24px)' } },
    MuiDialogContent: { [phone]: { padding: '12px 16px' } },
    MuiDialogTitle: { [phone]: { padding: '16px 16px 8px', fontSize: '1.1rem' } },
    MuiButton: { [phone]: { minHeight: 36 } },
  };
};

const componentOverrides = (theme) => {
  const { palette } = theme;
  const mobile = mobileComponentOverrides(theme);
  const border = `1px solid ${palette.divider}`;
  const isDark = palette.mode === 'dark';

  return {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          // Shared card styling: hairline ring + soft shadow. Pages use these
          // through boxShadow: 'var(--fx-card-shadow)'.
          '--fx-card-shadow': isDark
            ? `0 0 0 1px ${alpha('#fff', 0.08)}`
            : `0 0 0 1px ${alpha('#0f172a', 0.06)}, 0 1px 3px ${alpha('#0f172a', 0.06)}`,
          '--fx-card-shadow-hover': isDark
            ? `0 0 0 1px ${alpha('#fff', 0.12)}, 0 8px 24px ${alpha('#000', 0.4)}`
            : `0 0 0 1px ${alpha('#0f172a', 0.08)}, 0 8px 24px ${alpha('#0f172a', 0.08)}`,
        },
        body: {
          // Stop any single over-wide element from making the whole page scroll sideways
          overflowX: 'hidden',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          fontFeatureSettings: '"cv11", "ss01"',
        },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: alpha(palette.text.primary, 0.15),
          borderRadius: 8,
          border: `2px solid ${palette.background.default}`,
        },
        '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 12 },
        elevation0: { boxShadow: 'var(--fx-card-shadow)' },
        outlined: { border, boxShadow: 'none' },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { borderRadius: 12, boxShadow: 'var(--fx-card-shadow)' } },
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: 20, '&:last-child': { paddingBottom: 20 } } },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: {
        root: { backgroundColor: palette.background.paper, borderBottom: border, boxShadow: 'none' },
      },
    },
    MuiDrawer: {
      styleOverrides: { paper: { borderRight: border, boxShadow: 'none', borderRadius: 0 } },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          letterSpacing: 0,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          ...mobile.MuiButton,
        },
        sizeMedium: { padding: '7px 16px' },
        outlined: { borderColor: palette.divider, color: palette.text.primary, backgroundColor: palette.background.paper,
          '&:hover': { borderColor: alpha(palette.text.primary, 0.25), backgroundColor: palette.action.hover } },
        outlinedPrimary: { color: palette.primary.main },
        containedPrimary: { '&:hover': { backgroundColor: palette.primary.dark } },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiFab: {
      styleOverrides: { root: { textTransform: 'none', fontWeight: 600, boxShadow: theme.shadows[4] } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: palette.background.paper,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? alpha('#fff', 0.15) : '#d0d5dd' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(palette.text.primary, 0.35) },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1, borderColor: palette.primary.main },
          '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(palette.primary.main, 0.15)}` },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontWeight: 500 } },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500 },
        sizeSmall: { height: 24 },
      },
    },
    MuiTableContainer: {
      styleOverrides: { root: { overflowX: 'auto', WebkitOverflowScrolling: 'touch' } },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: isDark ? alpha('#fff', 0.03) : '#f8fafc',
            color: palette.text.secondary,
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            borderBottom: border,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: border,
          fontVariantNumeric: 'tabular-nums',
          ...mobile.MuiTableCell,
        },
        body: { fontSize: '0.8125rem', color: palette.text.primary },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover, &[style*="cursor"]:hover': { backgroundColor: palette.action.hover },
          '&:last-child > .MuiTableCell-body': { borderBottom: 0 },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16, boxShadow: theme.shadows[8], ...mobile.MuiDialogPaper },
      },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontWeight: 600, fontSize: '1.125rem', ...mobile.MuiDialogTitle } },
    },
    MuiDialogContent: {
      styleOverrides: { root: { ...mobile.MuiDialogContent } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '12px 24px 20px', gap: 8 } },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: alpha('#0f172a', 0.45), backdropFilter: 'blur(2px)' } },
      },
    },
    MuiMenu: {
      styleOverrides: { paper: { borderRadius: 10, boxShadow: theme.shadows[5], border } },
    },
    MuiPopover: {
      styleOverrides: { paper: { borderRadius: 10 } },
    },
    MuiMenuItem: {
      styleOverrides: { root: { fontSize: '0.875rem', borderRadius: 6, margin: '2px 6px' } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: '#0f172a', fontSize: '0.75rem', fontWeight: 500, borderRadius: 6, padding: '6px 10px' },
        arrow: { color: '#0f172a' },
      },
    },
    MuiTabs: {
      defaultProps: { variant: 'scrollable', scrollButtons: 'auto', allowScrollButtonsMobile: true },
      styleOverrides: { indicator: { height: 3, borderRadius: '3px 3px 0 0' } },
    },
    MuiTab: {
      styleOverrides: { root: { textTransform: 'none', fontWeight: 600, minHeight: 44 } },
    },
    MuiToggleButton: {
      styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 10 } },
    },
    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: 4 } },
    },
    MuiListItemButton: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: palette.divider } },
    },
  };
};

const buildTheme = (palette) => {
  const isDark = palette.mode === 'dark';
  const base = createTheme({
    palette,
    shape: { borderRadius: 4 },
    shadows: buildShadows(isDark ? '#000000' : '#0f172a'),
    typography: {
      fontFamily: FONT_FAMILY,
      h1: { fontWeight: 700, letterSpacing: '-0.025em' },
      h2: { fontWeight: 700, letterSpacing: '-0.025em' },
      h3: { fontWeight: 700, letterSpacing: '-0.02em' },
      h4: { fontWeight: 700, letterSpacing: '-0.02em', fontSize: '1.75rem' },
      h5: { fontWeight: 600, letterSpacing: '-0.015em', fontSize: '1.375rem' },
      h6: { fontWeight: 600, letterSpacing: '-0.01em', fontSize: '1.0625rem' },
      subtitle1: { fontWeight: 500 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600 },
      overline: { fontWeight: 600, letterSpacing: '0.08em' },
    },
  });
  return responsiveFontSizes(createTheme(base, { components: componentOverrides(base) }));
};

export const lightTheme = buildTheme({
  mode: 'light',
  primary: { main: '#2563eb', dark: '#1d4ed8', light: '#60a5fa', contrastText: '#ffffff' },
  secondary: { main: '#7c3aed' },
  success: { main: '#16a34a', dark: '#15803d', light: '#4ade80' },
  error: { main: '#dc2626', dark: '#b91c1c', light: '#f87171' },
  warning: { main: '#d97706', dark: '#b45309', light: '#fbbf24' },
  info: { main: '#0284c7' },
  background: { default: '#f6f7f9', paper: '#ffffff' },
  text: { primary: '#0f172a', secondary: '#64748b', disabled: '#94a3b8' },
  divider: '#e6e8ec',
  action: { hover: alpha('#0f172a', 0.04), selected: alpha('#2563eb', 0.08) },
});

export const darkTheme = buildTheme({
  mode: 'dark',
  primary: { main: '#60a5fa', dark: '#3b82f6', light: '#93c5fd', contrastText: '#0b1220' },
  secondary: { main: '#a78bfa' },
  background: { default: '#0b1220', paper: '#111a2e' },
  text: { primary: '#e2e8f0', secondary: '#94a3b8' },
  divider: alpha('#ffffff', 0.08),
});
